import {
  Announcement03,
  CheckCircle,
  CurrencyDollarCircle,
  Dataflow03,
  Delete,
  LineChartDown02,
  MessageTextSquare01,
  ReverseLeft,
  ShieldTick,
  SlashCircle01,
  SlashOctagon,
  Ticket01,
  UserX02,
  VideoRecorderOff,
} from "@untitledui/icons";
import type { ComponentType } from "react";

/**
 * Actions that go through the standard take action API endpoint
 */
export const API_TAKE_ACTION_TYPES = [
  "contact_creator",
  "remove_from_ad_campaign",
  "remove_from_collaboration",
  "demonetization_ticket",
  "takedown_request",
  "trigger_sequence",
] as const;

export type ApiTakeActionType = (typeof API_TAKE_ACTION_TYPES)[number];

/**
 * Actions that go through the standard log action API endpoint
 */
export const API_LOG_ACTION_TYPES = ["contact_creator", "video_demonetized", "no_action_required"] as const;

export type ApiLogActionType = (typeof API_LOG_ACTION_TYPES)[number];

/**
 * Actions you're about to take (IN_PROGRESS status) - displayed in UI
 * Includes actions handled by separate API endpoints (e.g., remove_from_collaboration)
 */
export const TAKE_ACTION_TYPES = [
  "contact_creator",
  "remove_from_ad_campaign",
  "remove_from_collaboration",
  "demonetization_ticket",
  "takedown_request",
] as const;

export type TakeActionType = (typeof TAKE_ACTION_TYPES)[number] | "trigger_sequence";

/**
 * The take actions the action menus offer. `demonetization_ticket` is absent: policies and
 * sequences still file those tickets, and their action records still render, but it cannot be
 * picked by hand. Filters and labels keep using the full list above.
 */
export const MANUAL_TAKE_ACTION_TYPES = TAKE_ACTION_TYPES.filter((type) => type !== "demonetization_ticket");

/**
 * `trigger_policy` is offered in the action menus but is never sent to the API or written to an
 * action record: it submits as `trigger_sequence` carrying a `policyId`, which the API resolves
 * to that policy's sequence. Anything that crosses the API boundary uses `TakeActionType`.
 */
export type UiTakeActionType = TakeActionType | "trigger_policy";

/**
 * Actions that have already been completed (COMPLETED or NO_ACTION status) - displayed in UI
 */
export const LOG_ACTION_TYPES = ["contact_creator", "video_demonetized", "no_action_required"] as const;

export type LogActionType = (typeof LOG_ACTION_TYPES)[number];

/**
 * All action types (union of take and log)
 */
export const ACTION_TYPES = [...TAKE_ACTION_TYPES, ...LOG_ACTION_TYPES] as const;

/**
 * Action records that qualify a video for the "With Creator to Action" list.
 * `trigger_sequence` is included because "Save & take action" enrolls the video in a
 * safety sequence first; the `contact_creator` record is only written later when the
 * sequence's send_message step runs. Without it, freshly-actioned videos surface in no
 * tab (UNB-2115). Pure TikTok notifications (demonetization_ticket / takedown_request)
 * are intentionally excluded.
 */
export const WITH_CREATOR_ACTIONS = ["contact_creator", "trigger_sequence"] as const;

/**
 * Creator-based bulk actions that report progress via SSE but don't flow through
 * the standard take/log action pipeline (no ActionRecords / FlagEdits).
 */
export type BulkCreatorActionType =
  | "message_creator"
  | "invite_to_collaboration"
  | "import_blocklist"
  | "apply_sample_rule";

export type ActionType = UiTakeActionType | LogActionType | BulkCreatorActionType;

export type ActionCategory = "take" | "log";

export const ADS_NOT_CONNECTED_TOOLTIP = "Available once ads are enabled in Settings → Integrations.";

export const getTakeActionLabel = (action: UiTakeActionType): string => {
  switch (action) {
    case "contact_creator":
      return "Send Message on TikTok";
    case "demonetization_ticket":
      return "Send Demonetisation Ticket to TikTok";
    case "takedown_request":
      return "Send Takedown request to TikTok";
    case "remove_from_collaboration":
      return "Remove from collaboration";
    case "remove_from_ad_campaign":
      return "Remove from ad campaign";
    case "trigger_sequence":
      return "Trigger Sequence";
    case "trigger_policy":
      return "Trigger Policy";
  }
};

export const getLogActionLabel = (action: ActionType | string): string => {
  switch (action) {
    case "contact_creator":
      return "Creator Contacted";
    case "demonetization_ticket":
      return "Demonetisation Ticket Sent";
    case "video_demonetized":
      return "Video Demonetised";
    case "takedown_request":
      return "Takedown Request Sent";
    case "remove_from_collaboration":
      return "Removed from collaboration";
    case "remove_from_ad_campaign":
      return "Removed from ad campaign";
    case "add_to_ad_campaign":
      return "Re-added to ad campaign";
    case "trigger_sequence":
      return "Enrolled in Policy";
    case "no_action_required":
      return "No Action required";
    case "risk_resolved":
      return "Risk Resolved";
    case "video_removed":
      return "Video Removed";
    case "creator_removed":
      return "Creator removed";
    case "exception_made_no_action_required":
      return "Exception";
    case "warning_sent":
      return "Warning sent";
    case "removed_gmv_max":
      return "Removed GMV MAX";
    case "warned_and_removed_gmv_max":
      return "Warned & removed GMV MAX";
    case "metadata_changed":
      return "Metadata changed";
    case "relationship_ended":
      return "Relationship ended";
    case "no_risks_confirmed":
      return "Manually reviewed - no risks found";
    case "no_risks_confirmation_undone":
      return "Manual review undone";
    default:
      return action;
  }
};

export const getActionTypeIcon = (action: ActionType | string): ComponentType<{ className?: string }> => {
  switch (action) {
    case "contact_creator":
      return MessageTextSquare01;
    case "demonetization_ticket":
      return Ticket01;
    case "video_demonetized":
      return LineChartDown02;
    case "takedown_request":
      return Delete;
    case "remove_from_collaboration":
      return Announcement03;
    case "remove_from_ad_campaign":
    case "add_to_ad_campaign":
      return CurrencyDollarCircle;
    case "trigger_sequence":
    case "trigger_policy":
    case "apply_sample_rule":
      return Dataflow03;
    case "no_action_required":
      return SlashCircle01;
    case "risk_resolved":
      return CheckCircle;
    case "video_removed":
      return VideoRecorderOff;
    case "creator_removed":
      return UserX02;
    case "exception_made_no_action_required":
      return SlashOctagon;
    case "warning_sent":
      return MessageTextSquare01;
    case "removed_gmv_max":
    case "warned_and_removed_gmv_max":
      return CurrencyDollarCircle;
    case "metadata_changed":
      return Announcement03;
    case "relationship_ended":
      return UserX02;
    case "no_risks_confirmed":
      return ShieldTick;
    case "no_risks_confirmation_undone":
      return ReverseLeft;
    default:
      return SlashCircle01;
  }
};
